import { useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import settings from "../../apiConfigs/settings";
import LazyImage from "../../designComponents/LazyImage";
import Modal from "../../designComponents/Modal";
import Typography from "../../designComponents/Typography";
import requestAPI from "../../utils/requestAPI";
import {
  AcceptButton,
  ButtonsSection,
  LabelText,
  ValueText,
} from "../Properties/Details/styles";
import AddStripeSettings from "./AddStripePayment";
import { PaymentMethodType } from "./ViewPaymentMethods";
interface PromoteProperty {
  perDayRate: number;
  weeklyDiscountRate: number;
  monthlyDiscountRate: number;
}

interface PlatformFee {
  percentageFee: number;
  flatFee: number;
  currency: string;
  paymentMethod?: string;
}

export interface PropertyPromotion {
  promoteProperty: PromoteProperty;
  platformFee: PlatformFee;
  stripeFee?: PlatformFee[];
  _id: string;
}

function ViewStripeFees({
  globalSettingsData,
  handleDelete,
  refetchData,
  paymentMethods,
}: {
  globalSettingsData?: PropertyPromotion;
  handleDelete?: (id: string) => void;
  refetchData: () => void;
  paymentMethods: PaymentMethodType[];
}) {
  const [settingsData, setSettings] = useState<PlatformFee>({
    percentageFee: 2,
    flatFee: 0.25,
    currency: "EUR",
    paymentMethod: "card",
  });

  const [addStripeVisible, setAddStripeVisible] = useState<{
    mode: "add" | "edit" | undefined;
    status: boolean;
  }>({ mode: undefined, status: false });

  const [removeStripeVisible, setRemoveStripeVisible] = useState<{
    title?: string;
    key?: string;
    status: boolean;
    loading?: boolean;
  }>({ status: false });

  const handleAddSettingStripeFee = async () => {
    try {
      await requestAPI(
        settings.addGlobalSettingStripeFee({
          flatFee: settingsData.flatFee,
          percentageFee: settingsData.percentageFee,
          currency: "EUR",
          paymentMethod: settingsData?.paymentMethod,
        })
      );
      refetchData?.();
      setAddStripeVisible({ mode: undefined, status: false });
      toast.success("Successfully Updated");
    } catch (error) {}
  };

  const cancelRemoval = () => {
    setRemoveStripeVisible({ status: false });
  };

  const handleRemoval = async () => {
    try {
      setRemoveStripeVisible((Prev) => ({ ...Prev, loading: true }));

      await requestAPI(
        settings.removeGlobalSettingStripeFee({
          paymentMethod: removeStripeVisible.key,
        })
      );
      refetchData?.();
      cancelRemoval();
    } catch (error: any) {
      if (typeof error?.message === "string") {
        alert(error?.message);
      }
      console.log("Stripe Fee Removal::", error);
    } finally {
      setRemoveStripeVisible((Prev) => ({ ...Prev, loading: false }));
    }
  };

  const stripeFees = globalSettingsData?.stripeFee;

  return (
    <>
      <div
        style={{
          display: "flex",
          gap: 20,
          width: "100%",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            borderBottomWidth: 1,
            borderBottomStyle: "dashed",
            display: "flex",
            flexDirection: "row",
            width: "100%",
            paddingBottom: 10,
            justifyContent: "space-between",
          }}
        >
          <div
            style={{ display: "flex", width: "100%", flexDirection: "column" }}
          >
            <Typography
              size={"largeTitle"}
              fontWeight="medium"
              style={{ marginBottom: 8 }}
            >
              Stripe Processing Fee
            </Typography>

            {stripeFees ? (
              <div
                style={{
                  display: "flex",
                  gap: "20px",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                {stripeFees?.map((sf) => {
                  const method = paymentMethods?.find(
                    (pm) => pm.key === sf?.paymentMethod
                  );
                  return (
                    <div key={sf.paymentMethod}>
                      <ButtonsSection>
                        <LabelText>Payment Method</LabelText>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                          }}
                        >
                          {method?.icon && (
                            <LazyImage
                              src={method?.icon}
                              alt="icon"
                              style={{ height: "20px", width: "20px" }}
                            />
                          )}
                          <Typography size={"title"} fontWeight="bold">
                            {method?.title ?? "Card"}
                          </Typography>
                          <ButtonStyled
                            onClick={() => {
                              setSettings(sf);
                              setAddStripeVisible({
                                mode: "edit",
                                status: true,
                              });
                            }}
                          >
                            Update
                          </ButtonStyled>
                          <ButtonStyled
                            $danger
                            onClick={() => {
                              setSettings(sf);
                              setRemoveStripeVisible({
                                key: sf.paymentMethod,
                                status: true,
                                title: method?.title,
                              });
                            }}
                          >
                            X
                          </ButtonStyled>
                        </div>
                      </ButtonsSection>
                      <ButtonsSection>
                        <LabelText>Stripe Percentage Fee </LabelText>
                        <ValueText>{sf?.percentageFee ?? 0}%</ValueText>
                      </ButtonsSection>
                      <ButtonsSection>
                        <LabelText>Stripe Flat Fee</LabelText>
                        <ValueText> €{sf?.flatFee ?? 0}</ValueText>
                      </ButtonsSection>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div>
                <Typography>No Stripe Fee Data</Typography>
              </div>
            )}
            {paymentMethods?.length !== stripeFees?.length && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  margin: "16px 0px",
                }}
              >
                <AcceptButton
                  onClick={() => {
                    setAddStripeVisible({ mode: "add", status: true });
                  }}
                >
                  Add
                </AcceptButton>
              </div>
            )}
          </div>
        </div>
      </div>
      <AddStripeSettings
        initialValues={settingsData}
        setData={setSettings}
        onAddPress={handleAddSettingStripeFee}
        isOpen={addStripeVisible.status}
        paymentMethods={
          addStripeVisible?.mode === "edit"
            ? paymentMethods?.filter(
                (pm) => pm.key === settingsData?.paymentMethod
              )
            : paymentMethods
        }
        onClose={() => {
          setAddStripeVisible({ mode: undefined, status: false });
        }}
      />
      <Modal
        isOpen={removeStripeVisible?.status}
        onClose={cancelRemoval}
        title={`Are you sure you want to remove, ${removeStripeVisible?.title}?`}
        halfWidth
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "30px",
            justifyContent: "center",
          }}
        >
          <ButtonStyled
            $danger
            style={{ height: "40px", width: "100px" }}
            onClick={handleRemoval}
            disabled={removeStripeVisible?.loading}
          >
            Yes
          </ButtonStyled>
          <ButtonStyled
            disabled={removeStripeVisible?.loading}
            style={{ height: "40px", width: "100px" }}
            onClick={cancelRemoval}
          >
            Cancel
          </ButtonStyled>
        </div>
      </Modal>
    </>
  );
}

export default ViewStripeFees;

const ButtonStyled = styled.button<{ $danger?: boolean }>`
  box-shadow: none;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  background-color: ${({ $danger }) =>
    $danger ? "hsl(10, 50%, 60%)" : "hsl(180, 50%, 90%)"};
  color: ${({ $danger }) =>
    $danger ? "hsl(10, 50%, 100%)" : "hsl(10, 50%, 10%)"};
  &:hover {
    background-color: hsl(180, 50%, 60%);
  }
`;
